import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { InputAdornment, OutlinedInput } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useDispatch } from 'react-redux';
import { encryptPassword } from '../../../utils/encryptPassword';
import BasicForm from '../basicForm/basicForm';
import colors from '../../../scss/colors';
import ButtonComponent from '../../common/buttons/ButtonComponent/ButtonComponent';
import { buttonVariants } from '../../common/buttons/types';
import { appNotificationsTypes } from '../../../utils/constants';
import api from '../../../api/apiSettings';
import { setNotification } from '../../../actions/appNotifications';
import { loginRequest } from '../../../authConfig';
import style from './finishForm.module.scss';
import { validatePassword } from '../../../utils/helpers';

const labelStyle = { fontSize: '13px', color: colors.DARKEST_GREY, margin: 0 };

const textFieldStyles = {
  backgroundColor: colors.WHITE,
  borderRadius: '10px',
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    height: '18px',
    padding: '14px 16px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E1E6EA',
    borderRadius: '10px'
  },
  color: colors.BLACK_TXT,
  fontSize: '14px',
  fontWeight: '325',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
};

const passwordRules = ['charactersqty', 'lowercase', 'uppercase', 'numberorsymbol'];

const SuccessCheck = () => (
  <span className={style.successIcon}>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m5 13 4 4L19 7" />
    </svg>
  </span>
);

function FinishForm(props) {
  const { cancel, step, totalStep, data, isUserExist, jobTitle } = props;

  const { instance } = useMsal();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [pass, setPass] = useState('');

  const [registering, setRegistering] = useState(false);

  const [passValid, setPassValid] = useState({
    charactersqty: false,
    lowercase: false,
    uppercase: false,
    numberorsymbol: false
  });

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validate = (password) => {
    setPassValid(validatePassword(password));
  };

  const changePasswordHandler = (e) => {
    validate(e.target.value);
    setPass(e.target.value);
  };

  const registerHandler = async () => {
    const regData = {
      FirstName: data.firstName,
      LastName: data.lastName,
      Phone: data.Phone,
      PhoneCountryCode: data.PhoneCountryCode,
      PrimaryEmail: data.email,
      Password: encryptPassword(pass)
    };

    try {
      setRegistering(true);
      await api.post('v1/user/quick-registration', regData);
      instance.loginRedirect({ ...loginRequest, redirectUri: `${window.location.origin}/dashboard` }).catch((e) => {
        console.error(e);
      });
    } catch (e) {
      dispatch(
        setNotification({
          type: appNotificationsTypes.ERROR,
          message: e?.response?.data?.title || e.message,
          duration: 3000
        })
      );
    }
    setRegistering(false);
  };

  useEffect(() => {
    window.localStorage.setItem('OpenedJobId', '');
  }, []);

  return (
    <BasicForm
      cancel={cancel}
      title={`${t('jobs.quickapply.userdetailheader')} ${jobTitle || ''}`}
      progress="100"
      subtitle={t('jobs.quickapply.applicationsent')}
      step={step}
      totalStep={totalStep}
    >
      <div className={style.head_wrapper}>
        <SuccessCheck />
        <p className={style.header}>{t('jobs.quickapply.thankyou')}</p>
        {isUserExist ? (
          <p className={style.sub_header}>{t('jobs.quickapply.applicationtorecruiterexist')}</p>
        ) : (
          <p className={style.sub_header}>{t('jobs.quickapply.applicationtorecruiter')}</p>
        )}
      </div>

      <div className={style.sessionCard}>
        <h5 className={style.cardTitle}>{t('jobs.quickapply.anotherroletitle')}</h5>
        <p className={style.cardText}>{t('jobs.quickapply.anotherroletext')}</p>
      </div>

      {!isUserExist && (
        <>
          <div className={style.registerCard}>
            <h5 className={style.cardTitle}>{t('jobs.quickapply.unlockautofill')}</h5>
            <p className={style.cardText}>{t('jobs.quickapply.unlockautofilltext')}</p>
            <div className={style.fieldContainer}>
              <label className={style.label} htmlFor="quickApplyPassword">
                {t('jobs.quickapply.choosepassword')}
              </label>
              <OutlinedInput
                id="quickApplyPassword"
                fullWidth
                value={pass}
                onChange={changePasswordHandler}
                placeholder={t('jobs.quickapply.passwordplaceholder')}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                sx={textFieldStyles}
              />
            </div>
            <div className={style.checkboxContainer}>
              {passwordRules.map((rule) => (
                <FormControlLabel
                  key={rule}
                  control={
                    <Checkbox
                      checked={passValid[rule]}
                      name={rule}
                      icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                      checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                    />
                  }
                  label={t(`common.passwordvalidation.${rule}`)}
                  sx={labelStyle}
                />
              ))}
            </div>
            <ButtonComponent
              disabled={Object.values(passValid).some((val) => !val) || registering}
              action={registerHandler}
              variant={buttonVariants.PRIMARY}
              classes="w-full"
              text={registering ? t('common.general.registering') : t('jobs.quickapply.createanaccount')}
            />
          </div>
          <div className={style.bottomContainer}>
            <Link to="/jobs">{t('jobs.quickapply.returnjobboard')}</Link>
          </div>
        </>
      )}
    </BasicForm>
  );
}

export default FinishForm;
